/**
 * Self-test suite for encrypted group membership events.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/GroupMembershipSelfTest.swift`
 * case-for-case and adds web-specific cases (unicode scalar counting,
 * IndexedDB isolation, SubtleCrypto HMAC paths).
 *
 * Sections:
 *   A. Codec + HMAC — wire-format round-trip and tamper resistance.
 *   B. Action-rule matrix — actor/subject constraints for all five actions.
 *   C. Monotonicity + idempotency.
 *   D. Queries + isolation.
 *   E. Server-replay resistance.
 *
 * All store-touching cases use isolated IDB databases so the suite is
 * safe on every dev launch without polluting production state.
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 * Auto-discovered by Vite `import.meta.glob` in `selftest/index.ts`.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import { GroupMasterKey } from "../group-identity/GroupIdentity";
import {
  MembershipEventCodec,
  MembershipCodecError,
  makeGroupMembershipPayload,
} from "./GroupMembershipEvent";
import {
  GroupMembershipStore,
  MembershipStoreError,
} from "./GroupMembershipStore";

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

let dbCounter = 0;

/** Each call returns a GroupMembershipStore backed by a fresh isolated IDB. */
function freshStore(): GroupMembershipStore {
  dbCounter += 1;
  return new GroupMembershipStore(
    `kaether:gmemb:selftest:${dbCounter}:${Date.now()}`,
  );
}

/**
 * Deterministic-but-distinct master keys per case index.
 * Mirrors iOS `freshMasterKey(_:)`.
 */
function freshMasterKey(caseIndex: number): GroupMasterKey {
  const bytes = new Uint8Array(32);
  for (let i = 0; i < 32; i++) {
    bytes[i] = ((i + 1) ^ (caseIndex + 1)) & 0xff;
  }
  return new GroupMasterKey(bytes);
}

/** base64url-encode a UTF-8 string (for crafting synthetic payloads). */
function b64url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Group membership (encrypted joins/leaves)",
  isVerified: true,
  run: async () =>
    buildSuiteResult(
      await Promise.all([
        // A — Codec + HMAC
        testCodecRoundTripFull(),
        testCodecRoundTripMinimal(),
        testCodecRoundTripAllActions(),
        testIsMembershipEventPrefix(),
        testTryDecodeReturnsNullForNonMembership(),
        testTamperedBytesRejected(),
        testWrongMasterKeyRejected(),
        testUnsupportedSchemaRejected(),
        testReasonOver200CharsRejectedOnEncode(),
        testReasonOver200CharsRejectedOnDecode(),

        // B — Action-rule matrix
        testBootstrapCreatesFounder(),
        testAddedMakesSubjectMember(),
        testJoinedViaInviteMakesSubjectMember(),
        testJoinedViaInviteRejectsActorNotEqualSubject(),
        testAddedRejectsActorEqualSubject(),
        testLeftMovesSubjectToWasMember(),
        testLeftRejectsActorNotEqualSubject(),
        testRemovedMovesSubjectToWasMember(),
        testRemovedRejectsActorEqualSubject(),
        testMasteredDoesNotChangeMembership(),

        // C — Monotonicity + idempotency
        testStaleAddedRejected(),
        testOutOfOrderLeftAfterRemovedRejected(),
        testReAddingWasMemberSucceeds(),
        testTwoMasteredEventsAccumulateInLog(),
        testRotationLogSortedByTsAscending(),

        // D — Queries + isolation
        testCurrentMembersOnlyMemberStateSorted(),
        testWasMembersOnlyWasMemberState(),
        testIsMemberConsistentWithCurrentMembers(),
        testTwoGroupsHaveIndependentState(),
        testPersistenceAcrossInstances(),

        // E — Server-replay resistance
        testReplayWithDifferentMasterKeyFailsAtDecode(),
        testReplayWithOldMasterKeyRejectedByMonotonicity(),
        testServerReorderingLaterTsWins(),
        testRotationLogContainsDepartureEvents(),
      ]),
    ),
});

// ---------------------------------------------------------------------------
// A — Codec + HMAC
// ---------------------------------------------------------------------------

async function testCodecRoundTripFull(): Promise<TestCase> {
  const name = "codec round-trips full payload (removed with reason)";
  try {
    const mk = freshMasterKey(0);
    const p = makeGroupMembershipPayload(
      "g1",
      "removed",
      "eve",
      "alice",
      1_700_000_000,
      "spam",
      null,
    );
    const encoded = await MembershipEventCodec.encode(p, mk);
    const decoded = await MembershipEventCodec.decode(encoded, mk);
    const ok =
      decoded.schema === 1 &&
      decoded.groupId === "g1" &&
      decoded.action === "removed" &&
      decoded.subjectUserId === "eve" &&
      decoded.actorUserId === "alice" &&
      decoded.ts === 1_700_000_000 &&
      decoded.reason === "spam" &&
      decoded.newGroupPublicIdAfterRotation === null;
    return ok ? pass(name) : fail(name, `got ${JSON.stringify(decoded)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testCodecRoundTripMinimal(): Promise<TestCase> {
  const name = "codec round-trips minimal payload (no reason, no rotation)";
  try {
    const mk = freshMasterKey(1);
    const p = makeGroupMembershipPayload("g1", "left", "dave", "dave", 100);
    const encoded = await MembershipEventCodec.encode(p, mk);
    const decoded = await MembershipEventCodec.decode(encoded, mk);
    const ok =
      decoded.groupId === "g1" &&
      decoded.action === "left" &&
      decoded.subjectUserId === "dave" &&
      decoded.actorUserId === "dave" &&
      decoded.ts === 100 &&
      decoded.reason === null &&
      decoded.newGroupPublicIdAfterRotation === null;
    return ok ? pass(name) : fail(name, `got ${JSON.stringify(decoded)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testCodecRoundTripAllActions(): Promise<TestCase> {
  const name = "codec round-trips all five action types";
  const mk = freshMasterKey(2);
  const pairs: Array<[string, string, string]> = [
    ["added", "bob", "alice"],
    ["joinedViaInvite", "carol", "carol"],
    ["left", "dave", "dave"],
    ["removed", "eve", "alice"],
    ["mastered", "alice", "alice"],
  ];
  try {
    for (const [action, subject, actor] of pairs) {
      const p = makeGroupMembershipPayload(
        "g",
        action as never,
        subject,
        actor,
        1,
      );
      const encoded = await MembershipEventCodec.encode(p, mk);
      const decoded = await MembershipEventCodec.decode(encoded, mk);
      if (decoded.action !== action || decoded.subjectUserId !== subject) {
        return fail(
          name,
          `action=${action}: got ${JSON.stringify(decoded)}`,
        );
      }
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testIsMembershipEventPrefix(): Promise<TestCase> {
  const name =
    "isMembershipEvent distinguishes gmember from chat/envelope/receipt/tomb/reply/pin/draft/SKDM/SKmsg/role";
  try {
    const mk = freshMasterKey(3);
    const p = makeGroupMembershipPayload("g", "added", "b", "a", 1);
    const encoded = await MembershipEventCodec.encode(p, mk);

    const positive = MembershipEventCodec.isMembershipEvent(encoded);
    const checks = [
      MembershipEventCodec.isMembershipEvent("hello world"),
      MembershipEventCodec.isMembershipEvent("kaether:e2ee:v1:foo"),
      MembershipEventCodec.isMembershipEvent("kaether:receipt:v1:abc"),
      MembershipEventCodec.isMembershipEvent("kaether:tombstone:v1:abc"),
      MembershipEventCodec.isMembershipEvent("kaether:reply:v1:abc"),
      MembershipEventCodec.isMembershipEvent("kaether:pin:v1:abc"),
      MembershipEventCodec.isMembershipEvent("kaether:draft:v1:abc"),
      MembershipEventCodec.isMembershipEvent("kaether:skdm:v1:abc"),
      MembershipEventCodec.isMembershipEvent("kaether:skmsg:v1:abc"),
      MembershipEventCodec.isMembershipEvent("kaether:role:v1:abc"),
    ];
    if (positive && checks.every((c) => !c)) {
      return pass(name);
    }
    return fail(name, `positive=${positive} negatives=${checks.join(",")}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testTryDecodeReturnsNullForNonMembership(): Promise<TestCase> {
  const name = "tryDecode returns null for non-membership plaintext";
  try {
    const mk = freshMasterKey(4);
    const r = await MembershipEventCodec.tryDecode("just a chat message", mk);
    if (r === null) return pass(name);
    return fail(name, `expected null, got ${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, `threw unexpectedly: ${e}`);
  }
}

async function testTamperedBytesRejected(): Promise<TestCase> {
  const name = "tampered bytes rejected (invalidGroupSignature or malformed)";
  try {
    const mk = freshMasterKey(5);
    const p = makeGroupMembershipPayload("g", "added", "b", "a", 1);
    const encoded = await MembershipEventCodec.encode(p, mk);

    // Flip a character near the end of the base64url body.
    const chars = [...encoded];
    const idx = chars.length - 5;
    chars[idx] = chars[idx] === "A" ? "B" : "A";
    const tampered = chars.join("");

    try {
      await MembershipEventCodec.decode(tampered, mk);
      return fail(name, "decode did not throw");
    } catch (e) {
      if (
        e instanceof MembershipCodecError &&
        (e.kind.tag === "invalidGroupSignature" ||
          e.kind.tag === "malformed")
      ) {
        return pass(name);
      }
      return fail(name, `wrong error type: ${e}`);
    }
  } catch (e) {
    return fail(name, `encode failed: ${e}`);
  }
}

async function testWrongMasterKeyRejected(): Promise<TestCase> {
  const name = "decode with wrong masterKey is rejected (invalidGroupSignature)";
  try {
    const mk = freshMasterKey(6);
    const other = freshMasterKey(7);
    const p = makeGroupMembershipPayload("g", "added", "b", "a", 1);
    const encoded = await MembershipEventCodec.encode(p, mk);

    try {
      await MembershipEventCodec.decode(encoded, other);
      return fail(name, "decode did not throw");
    } catch (e) {
      if (
        e instanceof MembershipCodecError &&
        e.kind.tag === "invalidGroupSignature"
      ) {
        return pass(name);
      }
      return fail(name, `wrong error: ${e}`);
    }
  } catch (e) {
    return fail(name, `encode failed: ${e}`);
  }
}

async function testUnsupportedSchemaRejected(): Promise<TestCase> {
  const name = "unsupported schema (99) is rejected";
  // Craft a payload with schema=99 bypassing encode (which only emits schema=1).
  const mk = freshMasterKey(8);
  const json = JSON.stringify({
    payload: {
      schema: 99,
      groupId: "g",
      action: "added",
      subjectUserId: "b",
      actorUserId: "a",
      ts: 1,
      reason: null,
      newGroupPublicIdAfterRotation: null,
    },
    sig: "AAAA",
  });
  const crafted = MembershipEventCodec.PREFIX + b64url(json);
  try {
    await MembershipEventCodec.decode(crafted, mk);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (
      e instanceof MembershipCodecError &&
      e.kind.tag === "unsupportedSchema" &&
      e.kind.version === 99
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

async function testReasonOver200CharsRejectedOnEncode(): Promise<TestCase> {
  const name = "encode rejects reason > 200 code points with reasonTooLong";
  const mk = freshMasterKey(9);
  const longReason = "x".repeat(201);
  const p = makeGroupMembershipPayload(
    "g",
    "removed",
    "b",
    "a",
    1,
    longReason,
  );
  try {
    await MembershipEventCodec.encode(p, mk);
    return fail(name, "encode did not throw");
  } catch (e) {
    if (
      e instanceof MembershipCodecError &&
      e.kind.tag === "reasonTooLong" &&
      e.kind.count === 201
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

async function testReasonOver200CharsRejectedOnDecode(): Promise<TestCase> {
  const name =
    "decode rejects maliciously-crafted wire payload with reason > 200 code points";
  // Craft a JSON envelope directly, bypassing the encode cap.
  const mk = freshMasterKey(10);
  const oversizeReason = "a".repeat(201);
  const json = JSON.stringify({
    payload: {
      schema: 1,
      groupId: "g",
      action: "removed",
      subjectUserId: "b",
      actorUserId: "a",
      ts: 1,
      reason: oversizeReason,
      newGroupPublicIdAfterRotation: null,
    },
    sig: "AAAA",
  });
  const crafted = MembershipEventCodec.PREFIX + b64url(json);
  try {
    await MembershipEventCodec.decode(crafted, mk);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (
      e instanceof MembershipCodecError &&
      e.kind.tag === "reasonTooLong"
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// B — Action-rule matrix
// ---------------------------------------------------------------------------

async function testBootstrapCreatesFounder(): Promise<TestCase> {
  const name = "bootstrap creates the founder as the first member";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    const isM = await s.isMember("g1", "alice");
    const members = await s.currentMembers("g1");
    const ok = isM && members.length === 1 && members[0] === "alice";
    return ok ? pass(name) : fail(name, `members=${JSON.stringify(members)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testAddedMakesSubjectMember(): Promise<TestCase> {
  const name = "added (alice→bob) makes bob a member";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    const r = await s.apply(
      makeGroupMembershipPayload("g1", "added", "bob", "alice", 100),
    );
    const isM = await s.isMember("g1", "bob");
    if (r.ok && r.record?.state === "member" && isM) return pass(name);
    return fail(name, `result=${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testJoinedViaInviteMakesSubjectMember(): Promise<TestCase> {
  const name = "joinedViaInvite (carol→carol) makes carol a member";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    const r = await s.apply(
      makeGroupMembershipPayload("g1", "joinedViaInvite", "carol", "carol", 100),
    );
    const isM = await s.isMember("g1", "carol");
    if (r.ok && r.record?.state === "member" && isM) return pass(name);
    return fail(name, `result=${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testJoinedViaInviteRejectsActorNotEqualSubject(): Promise<TestCase> {
  const name = "joinedViaInvite where actor != subject is illegalActor";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    const r = await s.apply(
      makeGroupMembershipPayload("g1", "joinedViaInvite", "carol", "alice", 100),
    );
    if (
      !r.ok &&
      r.error instanceof MembershipStoreError &&
      r.error.kind.tag === "illegalActor" &&
      r.error.kind.action === "joinedViaInvite"
    ) {
      return pass(name);
    }
    return fail(name, `result=${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testAddedRejectsActorEqualSubject(): Promise<TestCase> {
  const name = "added where actor == subject is illegalActor";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    const r = await s.apply(
      makeGroupMembershipPayload("g1", "added", "alice", "alice", 100),
    );
    if (
      !r.ok &&
      r.error instanceof MembershipStoreError &&
      r.error.kind.tag === "illegalActor" &&
      r.error.kind.action === "added"
    ) {
      return pass(name);
    }
    return fail(name, `result=${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testLeftMovesSubjectToWasMember(): Promise<TestCase> {
  const name = "left (dave→dave) moves dave to wasMember with leftAt = ts";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    await s.apply(makeGroupMembershipPayload("g1", "added", "dave", "alice", 100));
    const r = await s.apply(
      makeGroupMembershipPayload("g1", "left", "dave", "dave", 200),
    );
    const isM = await s.isMember("g1", "dave");
    const wasM = await s.wasMembers("g1");
    if (
      r.ok &&
      r.record?.state === "wasMember" &&
      r.record.leftAt === 200 &&
      !isM &&
      wasM.includes("dave")
    ) {
      return pass(name);
    }
    return fail(
      name,
      `result=${JSON.stringify(r)} isMember=${isM} wasMembers=${wasM}`,
    );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testLeftRejectsActorNotEqualSubject(): Promise<TestCase> {
  const name = "left where actor != subject is illegalActor";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    await s.apply(makeGroupMembershipPayload("g1", "added", "dave", "alice", 100));
    const r = await s.apply(
      makeGroupMembershipPayload("g1", "left", "dave", "alice", 200),
    );
    if (
      !r.ok &&
      r.error instanceof MembershipStoreError &&
      r.error.kind.tag === "illegalActor" &&
      r.error.kind.action === "left"
    ) {
      return pass(name);
    }
    return fail(name, `result=${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testRemovedMovesSubjectToWasMember(): Promise<TestCase> {
  const name = "removed (alice→eve) moves eve to wasMember";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    await s.apply(makeGroupMembershipPayload("g1", "added", "eve", "alice", 100));
    const r = await s.apply(
      makeGroupMembershipPayload("g1", "removed", "eve", "alice", 200, "spam"),
    );
    const isM = await s.isMember("g1", "eve");
    if (r.ok && r.record?.state === "wasMember" && r.record.leftAt === 200 && !isM) {
      return pass(name);
    }
    return fail(name, `result=${JSON.stringify(r)} isMember=${isM}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testRemovedRejectsActorEqualSubject(): Promise<TestCase> {
  const name = "removed where actor == subject is illegalActor";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    const r = await s.apply(
      makeGroupMembershipPayload("g1", "removed", "alice", "alice", 200),
    );
    if (
      !r.ok &&
      r.error instanceof MembershipStoreError &&
      r.error.kind.tag === "illegalActor" &&
      r.error.kind.action === "removed"
    ) {
      return pass(name);
    }
    return fail(name, `result=${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testMasteredDoesNotChangeMembership(): Promise<TestCase> {
  const name =
    "mastered returns ok(null) and does not change membership, appends to rotationLog";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    const beforeMembers = await s.currentMembers("g1");
    const r = await s.apply(
      makeGroupMembershipPayload(
        "g1",
        "mastered",
        "alice",
        "alice",
        300,
        null,
        "newpid",
      ),
    );
    const afterMembers = await s.currentMembers("g1");
    const log = await s.rotationLog("g1");
    const masteredEntry = log.find((e) => e.ts === 300 && e.newPublicId === "newpid");
    const ok =
      r.ok &&
      r.record === null &&
      JSON.stringify(beforeMembers) === JSON.stringify(afterMembers) &&
      masteredEntry !== undefined;
    return ok
      ? pass(name)
      : fail(
          name,
          `r=${JSON.stringify(r)} before=${JSON.stringify(beforeMembers)} after=${JSON.stringify(afterMembers)} log=${JSON.stringify(log)}`,
        );
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// C — Monotonicity + idempotency
// ---------------------------------------------------------------------------

async function testStaleAddedRejected(): Promise<TestCase> {
  const name = "stale added (older ts) rejected with staleTs";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    await s.apply(makeGroupMembershipPayload("g1", "added", "bob", "alice", 500));
    const r = await s.apply(
      makeGroupMembershipPayload("g1", "added", "bob", "alice", 100),
    );
    if (
      !r.ok &&
      r.error instanceof MembershipStoreError &&
      r.error.kind.tag === "staleTs" &&
      r.error.kind.claimed === 100 &&
      r.error.kind.current === 500
    ) {
      return pass(name);
    }
    return fail(name, `result=${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testOutOfOrderLeftAfterRemovedRejected(): Promise<TestCase> {
  const name = "out-of-order left (ts=200) after removed (ts=500) is rejected";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    await s.apply(makeGroupMembershipPayload("g1", "added", "eve", "alice", 100));
    await s.apply(makeGroupMembershipPayload("g1", "removed", "eve", "alice", 500));
    const r = await s.apply(
      makeGroupMembershipPayload("g1", "left", "eve", "eve", 200),
    );
    if (
      !r.ok &&
      r.error instanceof MembershipStoreError &&
      r.error.kind.tag === "staleTs" &&
      r.error.kind.claimed === 200 &&
      r.error.kind.current === 500
    ) {
      return pass(name);
    }
    return fail(name, `result=${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testReAddingWasMemberSucceeds(): Promise<TestCase> {
  const name = "re-adding a wasMember (ts=300) succeeds (state goes back to member)";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    await s.apply(makeGroupMembershipPayload("g1", "added", "bob", "alice", 100));
    await s.apply(makeGroupMembershipPayload("g1", "removed", "bob", "alice", 200));
    const r = await s.apply(
      makeGroupMembershipPayload("g1", "added", "bob", "alice", 300),
    );
    const isM = await s.isMember("g1", "bob");
    if (r.ok && r.record?.state === "member" && isM) return pass(name);
    return fail(name, `result=${JSON.stringify(r)} isMember=${isM}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testTwoMasteredEventsAccumulateInLog(): Promise<TestCase> {
  const name = "two mastered events both accumulate in rotationLog";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    await s.apply(
      makeGroupMembershipPayload("g1", "mastered", "alice", "alice", 100, null, "pid-1"),
    );
    await s.apply(
      makeGroupMembershipPayload("g1", "mastered", "alice", "alice", 200, null, "pid-2"),
    );
    const log = await s.rotationLog("g1");
    const m1 = log.find((e) => e.ts === 100 && e.newPublicId === "pid-1");
    const m2 = log.find((e) => e.ts === 200 && e.newPublicId === "pid-2");
    const ok = m1 !== undefined && m2 !== undefined;
    return ok ? pass(name) : fail(name, `log=${JSON.stringify(log)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testRotationLogSortedByTsAscending(): Promise<TestCase> {
  const name = "rotationLog returns entries sorted by ts ascending regardless of insertion order";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    // Insert out of order.
    await s.apply(
      makeGroupMembershipPayload("g1", "mastered", "alice", "alice", 300, null, "pid-c"),
    );
    await s.apply(
      makeGroupMembershipPayload("g1", "mastered", "alice", "alice", 100, null, "pid-a"),
    );
    await s.apply(
      makeGroupMembershipPayload("g1", "mastered", "alice", "alice", 200, null, "pid-b"),
    );
    const log = await s.rotationLog("g1");
    const masteredEntries = log.filter((e) => e.newPublicId !== "");
    const tsList = masteredEntries.map((e) => e.ts);
    const ok =
      tsList.length >= 3 &&
      tsList[tsList.length - 3] === 100 &&
      tsList[tsList.length - 2] === 200 &&
      tsList[tsList.length - 1] === 300;
    return ok ? pass(name) : fail(name, `tsList=${JSON.stringify(tsList)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// D — Queries + isolation
// ---------------------------------------------------------------------------

async function testCurrentMembersOnlyMemberStateSorted(): Promise<TestCase> {
  const name = "currentMembers returns only member-state, sorted lexicographically";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    await s.apply(makeGroupMembershipPayload("g1", "added", "carol", "alice", 100));
    await s.apply(makeGroupMembershipPayload("g1", "added", "bob", "alice", 200));
    await s.apply(makeGroupMembershipPayload("g1", "added", "eve", "alice", 300));
    await s.apply(makeGroupMembershipPayload("g1", "removed", "eve", "alice", 400));
    const members = await s.currentMembers("g1");
    const ok =
      members.length === 3 &&
      members[0] === "alice" &&
      members[1] === "bob" &&
      members[2] === "carol";
    return ok ? pass(name) : fail(name, `got ${JSON.stringify(members)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testWasMembersOnlyWasMemberState(): Promise<TestCase> {
  const name = "wasMembers returns only wasMember-state";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    await s.apply(makeGroupMembershipPayload("g1", "added", "bob", "alice", 100));
    await s.apply(makeGroupMembershipPayload("g1", "added", "eve", "alice", 200));
    await s.apply(makeGroupMembershipPayload("g1", "removed", "eve", "alice", 300));
    await s.apply(makeGroupMembershipPayload("g1", "left", "bob", "bob", 400));
    const wasM = await s.wasMembers("g1");
    const ok =
      wasM.length === 2 && wasM.includes("bob") && wasM.includes("eve");
    return ok ? pass(name) : fail(name, `got ${JSON.stringify(wasM)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testIsMemberConsistentWithCurrentMembers(): Promise<TestCase> {
  const name = "isMember is consistent with currentMembers";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    await s.apply(makeGroupMembershipPayload("g1", "added", "bob", "alice", 100));
    const members = await s.currentMembers("g1");
    const allTrue = await Promise.all(
      members.map((m) => s.isMember("g1", m)),
    );
    const ghostFalse = await s.isMember("g1", "ghost");
    if (allTrue.every(Boolean) && !ghostFalse) return pass(name);
    return fail(
      name,
      `allTrue=${allTrue} ghostFalse=${ghostFalse}`,
    );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testTwoGroupsHaveIndependentState(): Promise<TestCase> {
  const name = "two groups have independent state";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    await s.bootstrap("g2", "carol");
    await s.apply(makeGroupMembershipPayload("g1", "added", "bob", "alice", 100));
    const g1 = await s.currentMembers("g1");
    const g2 = await s.currentMembers("g2");
    const ok =
      g1.length === 2 &&
      g1.includes("alice") &&
      g1.includes("bob") &&
      g2.length === 1 &&
      g2[0] === "carol";
    return ok
      ? pass(name)
      : fail(name, `g1=${JSON.stringify(g1)} g2=${JSON.stringify(g2)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testPersistenceAcrossInstances(): Promise<TestCase> {
  const name = "membership state persists across store instances (write → reopen DB)";
  try {
    const dbName = `kaether:gmemb:selftest:persist:${Date.now()}`;
    const writer = new GroupMembershipStore(dbName);
    await writer.bootstrap("g1", "alice");
    await writer.apply(
      makeGroupMembershipPayload("g1", "added", "bob", "alice", 100),
    );

    // Open a new instance pointing at the same DB name.
    const reader = new GroupMembershipStore(dbName);
    const recovered = await reader.currentMembers("g1");
    const ok =
      recovered.length === 2 &&
      recovered.includes("alice") &&
      recovered.includes("bob");
    return ok
      ? pass(name)
      : fail(name, `recovered=${JSON.stringify(recovered)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// E — Server-replay resistance
// ---------------------------------------------------------------------------

async function testReplayWithDifferentMasterKeyFailsAtDecode(): Promise<TestCase> {
  const name = "forged event under different masterKey fails at decode";
  try {
    const realKey = freshMasterKey(20);
    const attackerKey = freshMasterKey(21);
    const p = makeGroupMembershipPayload(
      "g1",
      "added",
      "mallory",
      "alice",
      100,
    );
    const forged = await MembershipEventCodec.encode(p, attackerKey);

    try {
      await MembershipEventCodec.decode(forged, realKey);
      return fail(name, "decode did not throw");
    } catch (e) {
      if (
        e instanceof MembershipCodecError &&
        e.kind.tag === "invalidGroupSignature"
      ) {
        return pass(name);
      }
      return fail(name, `wrong error: ${e}`);
    }
  } catch (e) {
    return fail(name, `encode failed: ${e}`);
  }
}

async function testReplayWithOldMasterKeyRejectedByMonotonicity(): Promise<TestCase> {
  const name =
    "ex-member replay with valid sig under OLD master key is rejected by store monotonicity";
  try {
    const mk = freshMasterKey(22);
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    await s.apply(
      makeGroupMembershipPayload("g1", "added", "mallory", "alice", 100),
    );
    await s.apply(
      makeGroupMembershipPayload("g1", "removed", "mallory", "alice", 500),
    );

    // Mallory re-encodes their original "added" payload under the old mk.
    const replayPayload = makeGroupMembershipPayload(
      "g1",
      "added",
      "mallory",
      "alice",
      100,
    );
    const wire = await MembershipEventCodec.encode(replayPayload, mk);
    const decoded = await MembershipEventCodec.decode(wire, mk);

    // Codec accepts it (sig is valid), but the store rejects due to stale ts.
    const r = await s.apply(decoded);
    const stillOut = !(await s.isMember("g1", "mallory"));

    if (
      !r.ok &&
      r.error instanceof MembershipStoreError &&
      r.error.kind.tag === "staleTs" &&
      r.error.kind.claimed === 100 &&
      r.error.kind.current === 500 &&
      stillOut
    ) {
      return pass(name);
    }
    return fail(
      name,
      `result=${JSON.stringify(r)} stillOut=${stillOut}`,
    );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testServerReorderingLaterTsWins(): Promise<TestCase> {
  const name =
    "server reordering: ts=200 arrives first, ts=100 (stale) rejected — later-ts always wins";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    await s.apply(makeGroupMembershipPayload("g1", "added", "bob", "alice", 200));
    const staleResult = await s.apply(
      makeGroupMembershipPayload("g1", "added", "bob", "alice", 100),
    );
    const rec = await s.record("g1", "bob");
    const isM = await s.isMember("g1", "bob");
    const staleRejected =
      !staleResult.ok &&
      staleResult.error instanceof MembershipStoreError &&
      staleResult.error.kind.tag === "staleTs";
    const ok = staleRejected && rec?.lastEventTs === 200 && isM;
    return ok
      ? pass(name)
      : fail(
          name,
          `staleRejected=${staleRejected} lastEventTs=${rec?.lastEventTs} isM=${isM}`,
        );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testRotationLogContainsDepartureEvents(): Promise<TestCase> {
  const name =
    "rotationLog contains departure events (left/removed) as forward-secrecy signals";
  try {
    const s = freshStore();
    await s.bootstrap("g1", "alice");
    await s.apply(makeGroupMembershipPayload("g1", "added", "bob", "alice", 100));
    await s.apply(makeGroupMembershipPayload("g1", "added", "eve", "alice", 200));
    await s.apply(makeGroupMembershipPayload("g1", "left", "bob", "bob", 300));
    await s.apply(makeGroupMembershipPayload("g1", "removed", "eve", "alice", 400));
    const log = await s.rotationLog("g1");
    const ts300 = log.find((e) => e.ts === 300);
    const ts400 = log.find((e) => e.ts === 400);
    const ok = ts300 !== undefined && ts400 !== undefined;
    return ok ? pass(name) : fail(name, `log=${JSON.stringify(log)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}
